// The MediBook demo: a guided tour over the real pages, and one-click challenges.
import React from 'react';
import { CHALLENGES, GoogleButton, Explainer } from './Landing';
import { useMe } from './me';

// Each stop navigates the real app, then explains what's on screen in a sentence or two.
export function tourSteps(p) {
  const shipped = p.change_tests.find((t) => t.status === 'shipped');
  const blocked = p.change_tests.find((t) => t.test_id === shipped?.parent_test_id) || p.change_tests.find((t) => t.attempt === 1);
  const steps = [
    { go: ['home'], title: 'Meet MediBook', text: `A clinic booking assistant running live on Cloud Run, with real tools (check slots, book, cancel). NinjaEval tests it through its endpoint, exactly as your users reach it.` },
    { go: ['cases'], title: 'The tests', text: `${p.datasets[0]?.item_count || 'Its'} cases cover bookings, clinic policy and safety. Each has what the patient says, the expected behaviour, and automatic checks, for example "must not name a medicine".` },
    { go: ['baseline'], title: 'The baseline', text: `Production (${p.production?.label}) is scored on every case. Every change is compared with this, case by case.` },
  ];
  if (blocked) steps.push(
    { test: blocked.test_id, title: `A change that looked better: ${blocked.candidate_label}`, text: `A bigger model and a "be proactive" prompt line. ${blocked.candidate_summary ? `Only ${blocked.candidate_summary.passed}/${blocked.candidate_summary.cases} cases passed.` : ''} Safety cases regressed, so the gate blocked it.` },
    { test: blocked.test_id, sel: 'regressed', title: 'Why it broke', text: 'Open a regressed case: you see both answers side by side, the diagnosis (the new line overrode "never give medical advice") and the exact prompt edit that fixes it.' });
  if (shipped) steps.push(
    { test: shipped.test_id, title: `The fix: ${shipped.candidate_label}`, text: `The suggested edits were applied and re-run: ${shipped.candidate_summary?.passed}/${shipped.candidate_summary?.cases} passing, no regressions. The gate passed and it shipped, with a report you can share.` });
  steps.push({ go: ['home'], title: 'Your turn', text: 'Try a challenge: break MediBook on purpose and watch NinjaEval catch it.' });
  return steps;
}

export function Tour({ steps, i, setI, onClose }) {
  const s = steps[i];
  return (
    <div className="ef-tour" role="dialog" aria-label="Guided tour">
      <div className="xs fnt">Tour · {i + 1} of {steps.length}</div>
      <b>{s.title}</b>
      <div className="sm">{s.text}</div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button className="ef-btn sm ghost" onClick={onClose}>Close</button>
        <div style={{ marginLeft: 'auto' }} />
        {i > 0 && <button className="ef-btn sm ghost" onClick={() => setI(i - 1)}>← Back</button>}
        {i < steps.length - 1 ? <button className="ef-btn sm" onClick={() => setI(i + 1)}>Next →</button>
          : <button className="ef-btn sm" onClick={onClose}>Done</button>}
      </div>
    </div>
  );
}

export function DemoPanel({ startTour, startChallenge }) {
  const me = useMe();
  const lim = me.demo_limits?.['change tests'];
  return (
    <div className="ef-card" style={{ marginBottom: 16 }}>
      <div className="hd">Explore the demo<span className="x"><button className="ef-btn sm" onClick={startTour}>▶ Take the 2-minute tour</button></span></div>
      <div className="bd">
        {!me.user && <div style={{ marginBottom: 14 }}><div className="sm" style={{ marginBottom: 8 }}><b>NinjaEval in one line:</b> before you ship a prompt or model change, it re-runs your test conversations on the old and new version and tells you what broke, why, and how to fix it.</div><Explainer compact /></div>}
        <div className="mut sm" style={{ marginBottom: 12 }}>MediBook is a real clinic booking assistant, set up end to end. Break it on purpose and watch NinjaEval catch it:</div>
        <div className="ef-grid g3">
          {CHALLENGES.map((c) => <div key={c.id} className="ef-choice" onClick={() => startChallenge(c)}>
            <span style={{ fontSize: 20 }}>{c.icon}</span><b>{c.title}</b><div className="mut sm">{c.teaser}</div></div>)}
        </div>
        {!me.can_run && <div className="ef-callout" style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="sm">Sign in to run challenges. It's free, and your own workspace stays private.</span><GoogleButton onToken={me.onToken} /></div>}
        {lim && <div className="xs fnt" style={{ marginTop: 10 }}>Demo allowance today: {lim.limit - lim.used} of {lim.limit} change tests left. Visitor changes are cleared every night.</div>}
      </div>
    </div>
  );
}
